import React from "react";
import classNames from "classnames";
import { DetailList, type DetailListLayout } from "@shared/ui/detail-row";
import "./PickerOptionCard.scss";

/** Read-only характеристика опции с подписью. */
export type PickerOptionCardDetail = {
    /** Подпись поля (например «Наименование»). */
    label: string;
    /**
     * Значение. \
     * Пустые / `null` / `undefined` / `false` / `""` не рендерятся.
     */
    value?: React.ReactNode;
};

/** Пропсы {@link PickerOptionCard}. */
export interface PickerOptionCardProps {
    /** Основной заголовок опции. */
    title: React.ReactNode;
    /**
     * Характеристики с метками через {@link DetailList}. \
     * Пустые значения отфильтровываются.
     */
    details?: readonly PickerOptionCardDetail[];
    /**
     * Layout списка характеристик. \
     * Как у list-item: `inline` | `stacked` | `tiles`.
     * @default "inline"
     */
    detailsLayout?: DetailListLayout;
    /** Доп. CSS-класс корня. */
    className?: string;
}

function hasDetailValue(value: React.ReactNode | undefined): boolean {
    return value != null && value !== false && value !== "";
}

/**
 * Компактная «плитка» опции для fullscreen picker. \
 * Паттерн как у list-item: title + {@link DetailList} с label/value. \
 * Владеет card chrome (surface, radius, padding); gap между карточками — \
 * `padding-block-end` на `.spp-ui-list-box__item` в omega-ui theme (virtual-safe). \
 * Hover/selected — у list-box item через theme.
 *
 * @example
 * ```tsx
 * <PickerOptionCard
 *   title="Ц01"
 *   details={[
 *     { label: "Наименование", value: "Цех сборки" },
 *     { label: "Цех. код", value: "01" },
 *   ]}
 * />
 * ```
 */
export function PickerOptionCard({ title, details, detailsLayout = "inline", className }: PickerOptionCardProps) {
    const rows = (details ?? []).filter((detail) => hasDetailValue(detail.value));

    return (
        <div
            className={classNames(
                "omp-picker-option-card",
                detailsLayout === "tiles" && "omp-picker-option-card--tiles",
                className,
            )}
        >
            <div className="omp-picker-option-card__title">{title}</div>
            {rows.length > 0 ? (
                <DetailList className="omp-picker-option-card__details" layout={detailsLayout}>
                    {rows.map((detail, index) => (
                        <DetailList.Row key={`${detail.label}-${index}`} label={detail.label} value={detail.value} />
                    ))}
                </DetailList>
            ) : null}
        </div>
    );
}
